/**
 * Remark (mdast) plugin that repairs the *mechanical* corruptions which make
 * Coco's Mermaid diagrams fail to parse before the source ever reaches
 * `mermaid.render`.
 *
 * Streamdown hands the raw fenced ```mermaid block straight to Mermaid with no
 * pre-processing, and Mermaid's flowchart grammar is unforgiving: a single
 * Unicode arrow (`→`, `—>`) or a Graphviz-style single-dash edge (`->`) throws
 * and the whole diagram renders as a red "Parse error" card.
 *
 * This pass is deliberately conservative. It only rewrites glyphs that are
 * *never* valid Mermaid syntax, so it can never turn a good diagram into a bad
 * one. Structural mistakes — mismatched brackets, unquoted labels, raw emails
 * used as node ids — are the model's job to avoid (see the Mermaid guidance in
 * the Coco system prompt); they cannot be repaired safely by string surgery and
 * are intentionally out of scope here.
 *
 * Kept pure (no React or unified imports) so the normalization can be unit
 * tested in isolation — see remark-sanitize-mermaid.spec.ts.
 */

/**
 * Minimal structural subset of the mdast nodes this plugin touches. We avoid
 * importing from `mdast` (the `@types/mdast` package): it is only a transitive
 * type dependency, so it resolves in local editors but fails CI type-check and
 * build with TS2307 ("Cannot find module 'mdast'"). The plugin only needs to
 * find fenced `mermaid` code blocks and recurse into parents, so these fields
 * cover everything it reads.
 */
export interface MermaidTreeNode {
  type: string;
  lang?: string | null;
  value?: string;
  children?: MermaidTreeNode[];
}

/**
 * Rewrite the body of a single Mermaid diagram, normalizing the corruptions
 * that most often break parsing while leaving valid syntax untouched.
 */
export function normalizeMermaidSource(source: string): string {
  return (
    source
      // "Smart" punctuation the tokenizer never accepts. LLMs emit curly
      // quotes freely; Mermaid only understands the straight ASCII forms.
      .replace(/[“”]/g, '"')
      .replace(/[‘’]/g, "'")
      // Edge operators mangled into dash-like Unicode: a run of figure/en/em
      // dash or horizontal bar followed by `>` (e.g. `—>`, `–>`, `―>`) is an
      // arrow the model meant to write as `-->`.
      .replace(/[‒-―]+>/g, '-->')
      // Unicode right-arrow glyph, with or without a trailing `>` (`→`, `→>`).
      // A bare `→` in a flowchart is always an edge, so this is safe.
      .replace(/→+>?/g, '-->')
      // Single-dash ASCII arrow `->` (a Graphviz habit) becomes Mermaid's
      // `-->`. The leading group `(^|[^-<>.=])` is a hand-rolled "not preceded
      // by" guard (JS lookbehind is avoided for older-engine safety): the char
      // before `->` must be start-of-line or something that is NOT part of an
      // already-valid edge glyph — `-` (`-->`, `--`), `<` (`<--`), `.` (`-.->`)
      // or `=` (`==>`). The trailing `(?!>)` stops us matching the `->` inside
      // `-->`/`->>`. `$1` re-emits the captured guard char. `m` flag so `^`
      // anchors each line of a multi-line diagram.
      .replace(/(^|[^-<>.=])->(?!>)/gm, '$1-->')
  );
}

/**
 * A fenced block is Mermaid when its info-string *starts* with `mermaid`.
 * mdast puts the first info-string token in `lang` and the rest in `meta`, so
 * ` ```mermaid ` gives `lang === 'mermaid'`, but variants like ` ```mermaid:foo `
 * (no space, so the whole token lands in `lang`) would slip past an exact
 * `=== 'mermaid'` check. The `\b` keeps `mermaidjs` and similar from matching.
 */
export function isMermaidLang(lang: string | null | undefined): boolean {
  return typeof lang === 'string' && /^\s*mermaid\b/i.test(lang);
}

/** Recursively normalize every ```mermaid code block in the tree. */
function walk(node: MermaidTreeNode): void {
  if (
    node.type === 'code' &&
    isMermaidLang(node.lang) &&
    node.value !== undefined
  ) {
    node.value = normalizeMermaidSource(node.value);
    return;
  }
  if (node.children !== undefined) {
    for (const child of node.children) {
      walk(child);
    }
  }
}

/**
 * Runs alongside Streamdown's default remark plugins (see
 * RESPONSE_REMARK_PLUGINS). Order does not matter: it touches only `code` nodes
 * whose language is `mermaid`, which no other plugin rewrites.
 */
export function remarkSanitizeMermaid(): (tree: MermaidTreeNode) => void {
  return (tree: MermaidTreeNode): void => {
    walk(tree);
  };
}
